import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  List,
  Table,
  Tag,
  Typography,
} from 'antd';
import ReactECharts from 'echarts-for-react';
import type { PersonalReportSummaryJson } from '../../data/mockPersonalReportSummary';
import { AbilityAnalysis } from './AbilityAnalysis';
import type { MultiViewProfile } from './multiViewProfile';
import {
  competencyBand,
  scoreEffectiveDim,
  splitNumberedActions,
} from './shared';

function SummaryParagraph({ text }: { text: string }) {
  if (!/[①②③④⑤⑥⑦⑧⑨⑩]/.test(text)) {
    return <Typography.Paragraph className="report-summary-para">{text}</Typography.Paragraph>;
  }
  const labelMatch = text.match(/^([^①②③④⑤⑥⑦⑧⑨⑩]+?[：:])\s*/);
  const label = labelMatch?.[1] ?? '';
  const rest = labelMatch ? text.slice(labelMatch[0].length) : text;
  const items = rest
    .split(/(?=[①②③④⑤⑥⑦⑧⑨⑩])/)
    .map((s) => s.replace(/[；;]\s*$/, '').trim())
    .filter(Boolean);
  if (items.length <= 1) {
    return <Typography.Paragraph className="report-summary-para">{text}</Typography.Paragraph>;
  }
  return (
    <div className="report-summary-circled">
      {label ? <div className="report-summary-circled-label">{label}</div> : null}
      <ul className="report-summary-circled-list">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function SectionTitle({
  index,
  title,
  subtitle,
  color,
}: {
  index: string;
  title: string;
  subtitle?: string;
  color: string;
}) {
  return (
    <div className="report-section-title">
      <div className="report-section-index" style={{ color }}>
        {index} /
      </div>
      <div className="report-section-text">
        <div className="report-section-heading">{title}</div>
        {subtitle ? <div className="report-section-sub">{subtitle}</div> : null}
      </div>
    </div>
  );
}

function LazyEChart({
  option,
  height,
  active,
}: {
  option: Record<string, unknown>;
  height: number;
  active: boolean;
}) {
  if (!active) {
    return <div style={{ height, background: 'var(--evidence-bg)', borderRadius: 8 }} />;
  }
  return (
    <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />
  );
}

function computeRoleAverages(
  rows: NonNullable<PersonalReportSummaryJson['evaluationBreakdown']>,
) {
  const map = new Map<string, { sum: number; count: number }>();
  for (const row of rows) {
    const dimCount = row.dimensions.length || 1;
    const avg = row.avgPerDim ?? row.rowTotal / dimCount;
    const prev = map.get(row.roleLabel) ?? { sum: 0, count: 0 };
    map.set(row.roleLabel, { sum: prev.sum + avg, count: prev.count + 1 });
  }
  return [...map.entries()]
    .map(([roleLabel, { sum, count }]) => ({
      roleLabel,
      avg: Math.round((sum / count) * 100) / 100,
      count,
    }))
    .sort((a, b) => b.avg - a.avg);
}

export function EvidenceDetail({
  data,
  effectiveRadar,
  multiViewProfile,
  onGenerateNarrative,
  generateLoading = false,
}: {
  data: PersonalReportSummaryJson;
  effectiveRadar: PersonalReportSummaryJson['radar'];
  multiViewProfile: MultiViewProfile | null;
  onGenerateNarrative?: () => void;
  generateLoading?: boolean;
}) {
  const {
    emp,
    dimensionComparison,
    evaluationBreakdown,
    qualityAnalysisMeta,
    competencyRadar,
    performanceScore,
    leaderComment,
  } = data;
  const qa = qualityAnalysisMeta ?? {
    advantageMeanThreshold: 5.5,
    concernMeanThreshold: 4,
    desc: '',
  };
  const nb = emp.nineBoxResults?.[0];
  const competency = data.competencyAssessment ?? emp.competencyAssessments?.[0];
  const competencyEvaluatorLabel =
    competency && 'evaluatorName' in competency && competency.evaluatorName != null
      ? String(competency.evaluatorName)
      : competency && 'evaluator' in competency && competency.evaluator?.name != null
        ? String(competency.evaluator.name)
        : undefined;
  const hp = emp.highPotentialAsEvaluatee?.[0];

  const radarIndicators = effectiveRadar.map((r) => ({
    name: (r.label || r.code || '?').slice(0, 12),
    max: 7,
  }));
  const radarValues = effectiveRadar.map((r) => r.avg);

  const multiEvaluatorRadarOption = useMemo(() => {
    const rows = evaluationBreakdown ?? [];
    if (!effectiveRadar.length || rows.length < 2) return null;
    const overlayRows = rows.filter((row) =>
      effectiveRadar.every((er) => scoreEffectiveDim(row, er) != null),
    );
    if (overlayRows.length < 2) return null;
    // 高对比色相：蓝 / 橙 / 青绿 / 品红 / 黄 / 紫 / 青 / 棕红，避免同系灰绿混在一起
    const palette = [
      '#2563eb',
      '#ea580c',
      '#059669',
      '#db2777',
      '#ca8a04',
      '#7c3aed',
      '#0891b2',
      '#b45309',
    ];
    const evalData = overlayRows.map((r, idx) => {
      const color = palette[idx % palette.length];
      return {
        value: effectiveRadar.map((er) => scoreEffectiveDim(r, er)!),
        name: `${r.roleLabel}·${r.evaluatorName}`,
        lineStyle: { width: 2.5, color },
        areaStyle: { opacity: 0.1, color },
        itemStyle: { color },
        symbolSize: 6,
      };
    });
    const meanPolygon = {
      value: radarValues,
      name: '360 综合（均值）',
      lineStyle: { type: 'dashed' as const, width: 2.5, color: '#1f2937' },
      itemStyle: { color: '#1f2937' },
      areaStyle: { opacity: 0 },
      symbolSize: 7,
      z: 20,
    };
    return {
      color: palette,
      tooltip: { trigger: 'item' as const },
      legend: { type: 'scroll' as const, bottom: 0, itemGap: 10, textStyle: { fontSize: 11 } },
      radar: { indicator: radarIndicators, radius: '58%', splitNumber: 7, axisName: { fontSize: 11 } },
      series: [{ type: 'radar' as const, emphasis: { focus: 'series' as const }, data: [...evalData, meanPolygon] }],
    };
  }, [evaluationBreakdown, effectiveRadar, radarIndicators, radarValues]);

  const simpleMeanRadarOption = useMemo(
    () => ({
      tooltip: {},
      radar: { indicator: radarIndicators },
      series: [{ type: 'radar' as const, areaStyle: { opacity: 0.35 }, data: [{ value: radarValues, name: '360 综合' }] }],
    }),
    [radarIndicators, radarValues],
  );

  const competencyOption = useMemo(() => {
    if (!competencyRadar?.length) return null;
    const max = 5;
    return {
      tooltip: {},
      color: ['#176B5B'],
      radar: {
        indicator: competencyRadar.map((r) => ({ name: r.axis.slice(0, 10), max })),
        radius: '62%',
        splitNumber: 5,
        axisName: { fontSize: 10, color: '#71807a' },
        splitLine: { lineStyle: { color: '#dde3e0' } },
        splitArea: { areaStyle: { color: ['#fffefa', '#f1f3f2'] } },
        axisLine: { lineStyle: { color: '#afcbc2' } },
      },
      series: [{
        type: 'radar' as const,
        areaStyle: { opacity: 0.18, color: '#176B5B' },
        lineStyle: { width: 1.5, color: '#176B5B' },
        itemStyle: { color: '#176B5B' },
        data: [{ value: competencyRadar.map((r) => r.score), name: '胜任力' }],
      }],
    };
  }, [competencyRadar]);

  const competencySorted = useMemo(
    () => [...(competencyRadar ?? [])].sort((a, b) => b.score - a.score),
    [competencyRadar],
  );
  const competencyTop3 = competencySorted.slice(0, 3);
  const competencyBottom2 =
    competencySorted.length >= 5
      ? [...competencySorted].reverse().filter((r) => !competencyTop3.includes(r)).slice(0, 2)
      : [];

  const qualityOverallAnalysis = useMemo<string[]>(() => {
    const rows = evaluationBreakdown ?? [];
    const cmpList = dimensionComparison ?? [];
    if (!rows.length || !effectiveRadar.length) return [];
    const labelOf = (r: (typeof effectiveRadar)[number]) => r.label || r.code || '—';
    const sortedRadar = [...effectiveRadar].sort((a, b) => b.avg - a.avg);
    const overallAvg = effectiveRadar.reduce((a, b) => a + b.avg, 0) / effectiveRadar.length;
    const advantages = sortedRadar.filter((r) => r.avg >= qa.advantageMeanThreshold);
    const concerns = sortedRadar.filter((r) => r.avg < qa.concernMeanThreshold);
    const normal = sortedRadar.filter((r) => r.avg >= qa.concernMeanThreshold && r.avg < qa.advantageMeanThreshold);
    const top2 = sortedRadar.slice(0, 2);
    const bottom2 = sortedRadar.slice(-2).reverse();
    const totals = rows.map((r) => r.rowTotal);
    const totalRange = totals.length ? Math.max(...totals) - Math.min(...totals) : 0;
    const sortedRows = [...rows].sort((a, b) => b.rowTotal - a.rowTotal);
    const topEval = sortedRows[0];
    const bottomEval = sortedRows[sortedRows.length - 1];
    const selfRow = rows.find((r) => r.roleLabel === '自评');
    const othersRows = rows.filter((r) => r.roleLabel !== '自评');
    const othersTotalAvg = othersRows.length ? othersRows.reduce((a, b) => a + b.rowTotal, 0) / othersRows.length : null;
    const selfGapTotal = selfRow && othersTotalAvg != null ? selfRow.rowTotal - othersTotalAvg : null;
    type DimCmp = NonNullable<typeof dimensionComparison>[number];
    const cmpWithSpread = cmpList.filter((c): c is DimCmp & { spread: number } => c.spread != null);
    const maxPosSpread = cmpWithSpread.filter((c) => c.spread > 0).sort((a, b) => b.spread - a.spread)[0];
    const maxNegSpread = cmpWithSpread.filter((c) => c.spread < 0).sort((a, b) => a.spread - b.spread)[0];
    const dimRanges = effectiveRadar.map((r) => {
      const scores = rows.map((row) => row.dimensions.find((d) => d.dimName === labelOf(r))?.score).filter((s): s is number => s != null);
      const range = scores.length ? Math.max(...scores) - Math.min(...scores) : 0;
      return { name: labelOf(r), range };
    });
    const sortedDimRange = [...dimRanges].sort((a, b) => b.range - a.range);
    const mostDispersed = sortedDimRange[0];
    const lines: string[] = [];
    const nDim = effectiveRadar.length || rows[0]?.dimensions.length || 6;
    lines.push(`① 整体水位：${nDim} 项素质 360° 均分 ${overallAvg.toFixed(2)} 分（标尺 0～7），其中相对优势维度 ${advantages.length} 项、常规 ${normal.length} 项、需重点改进 ${concerns.length} 项；行合计在 ${rows.length} 名评价人之间的极差 ${totalRange.toFixed(1)} 分（最高 ${topEval.roleLabel}「${topEval.evaluatorName}」${topEval.rowTotal.toFixed(1)} / 最低 ${bottomEval.roleLabel}「${bottomEval.evaluatorName}」${bottomEval.rowTotal.toFixed(1)}）。`);
    lines.push(`② 强项画像：均分前 2 项为${top2.map((r) => `「${labelOf(r)}」${r.avg.toFixed(2)}`).join('、')}${advantages.length ? `，已达「优势」阈值（≥ ${qa.advantageMeanThreshold}）` : '，但尚未达到「优势」阈值（≥ ' + qa.advantageMeanThreshold + '），属于稳健正向区间'}；需重点关注的后 2 项为${bottom2.map((r) => `「${labelOf(r)}」${r.avg.toFixed(2)}`).join('、')}${concerns.length ? `，其中 ${concerns.map((r) => `「${labelOf(r)}」`).join('、')}已落入短板区间（< ${qa.concernMeanThreshold}），需在下一周期纳入个人发展计划` : '，整体未触发短板线，但偏离均值较多，是后续提升的优先抓手'}。`);
    if (selfRow && othersTotalAvg != null && selfGapTotal != null) {
      const direction = selfGapTotal > 1 ? '显著乐观' : selfGapTotal > 0.3 ? '略偏乐观' : selfGapTotal < -1 ? '显著保守' : selfGapTotal < -0.3 ? '略偏保守' : '与他评接近';
      const detail: string[] = [];
      if (maxPosSpread) detail.push(`自评最高于他评的维度为「${maxPosSpread.dimName}」(差 +${maxPosSpread.spread.toFixed(2)})`);
      if (maxNegSpread) detail.push(`自评最低于他评的维度为「${maxNegSpread.dimName}」(差 ${maxNegSpread.spread.toFixed(2)})`);
      lines.push(`③ 自评 vs 他评：自评行合计 ${selfRow.rowTotal.toFixed(1)} 分，非自评（直属/本部门/跨部门）均值 ${othersTotalAvg.toFixed(2)} 分，整体${direction}（差 ${selfGapTotal >= 0 ? '+' : ''}${selfGapTotal.toFixed(2)}）${detail.length ? '；' + detail.join('；') : ''}。`);
    }
    if (othersRows.length >= 2) {
      const direct = rows.find((r) => r.roleLabel === '直属领导');
      const within = rows.find((r) => r.roleLabel === '本部门同事');
      const cross = rows.find((r) => r.roleLabel === '跨部门同事');
      const parts: string[] = [];
      if (direct) parts.push(`直属 ${direct.rowTotal.toFixed(1)}`);
      if (within) parts.push(`本部门 ${within.rowTotal.toFixed(1)}`);
      if (cross) parts.push(`跨部门 ${cross.rowTotal.toFixed(1)}`);
      const crossNote = direct && cross && direct.rowTotal - cross.rowTotal >= 2 ? '；跨部门视角明显保守于直属，提示对外协作的"可见度"是阻力点' : direct && cross && cross.rowTotal - direct.rowTotal >= 2 ? '；跨部门视角反而高于直属，提示对外形象优于内部预期，可借此扩大影响半径' : '';
      const maxTotal = (effectiveRadar.length || 6) * 7;
      lines.push(`④ 跨视角对齐：${parts.join(' / ')}（标尺 0～7 × ${effectiveRadar.length || 6} 项 = 满分 ${maxTotal}）${crossNote}。整体跨视角共识度${totalRange < 3 ? '较好' : totalRange < 5 ? '一般，建议在关键项目复盘环节统一口径' : '偏弱，建议优先组织一次跨部门对齐复盘'}。`);
    }
    if (mostDispersed && mostDispersed.range >= 1.5) {
      lines.push(`⑤ 评价分歧聚焦：「${mostDispersed.name}」在不同评价人之间分差最大（极差 ${mostDispersed.range.toFixed(1)}），是本期最值得追问的维度——同一行为在不同视角下被读出不同含义，需在 1on1 中明确该维度的事实证据与期望口径。`);
    } else if (mostDispersed) {
      lines.push(`⑤ 评价分歧聚焦：各维度跨视角极差均 ≤ ${mostDispersed.range.toFixed(1)}，评价口径整体收敛，所得结论稳健，可作为后续发展的高置信度参照。`);
    }
    return lines;
  }, [evaluationBreakdown, dimensionComparison, effectiveRadar, qa.advantageMeanThreshold, qa.concernMeanThreshold]);

  const competencyHpAnalysis = useMemo<string[]>(() => {
    if (!competencyRadar?.length && !hp && !competency) return [];
    const lines: string[] = [];
    const compRows = competencyRadar ?? [];
    const compAvg = compRows.length ? compRows.reduce((a, b) => a + b.score, 0) / compRows.length : null;
    const sortedComp = [...compRows].sort((a, b) => b.score - a.score);
    const topComp = sortedComp[0];
    const bottomComp = sortedComp[sortedComp.length - 1];
    const compRange = compRows.length > 1 ? topComp.score - bottomComp.score : 0;
    const weighted = competency?.weightedScore != null ? Number(competency.weightedScore) : null;
    const evaluator = competencyEvaluatorLabel;
    if (compAvg != null && weighted != null) {
      lines.push(`① 胜任力总览：${compRows.length} 项岗位胜任力均分 ${compAvg.toFixed(2)} 分（标尺 1～5），加权综合分 ${weighted.toFixed(2)} 分，整体处于「${competencyBand(weighted)}」段；评价人${evaluator ? `为「${evaluator}」` : '为直属领导'}，单视角口径，与素质项 360° 多源结论需相互参照。`);
    } else if (weighted != null && !compRows.length) {
      lines.push(`① 胜任力总览：本期接口未返回分项维度得分（无 radar 轴数据），暂以加权综合分 ${weighted.toFixed(2)} 分为准（「${competencyBand(weighted)}」）${evaluator ? `；评价人「${evaluator}」` : ''}。分项雷达图需在胜任力评价的维度得分写入 dimScores 后方可绘制，与管理端导出 PDF 一致。`);
    } else if (compAvg != null) {
      lines.push(`① 胜任力总览：${compRows.length} 项岗位胜任力均分 ${compAvg.toFixed(2)} 分（标尺 1～5），整体处于「${competencyBand(compAvg)}」段。`);
    }
    if (topComp && bottomComp && topComp !== bottomComp) {
      const dispNote = compRange >= 0.8 ? '差距偏大，提示能力结构呈现明显倾向' : compRange >= 0.4 ? '存在一定差距，整体均衡偏正向' : '各项分布均衡，无明显短板';
      lines.push(`② 强弱项：「${topComp.axis}」最高 ${topComp.score.toFixed(2)}，「${bottomComp.axis}」相对最低 ${bottomComp.score.toFixed(2)}（极差 ${compRange.toFixed(2)}），${dispNote}。「${bottomComp.axis}」是岗位维度上下一周期的优先提升点。`);
    }
    if (compRows.length && effectiveRadar.length) {
      const overall360 = effectiveRadar.reduce((a, b) => a + b.avg, 0) / effectiveRadar.length;
      const compStdOnSeven = compAvg != null ? (compAvg / 5) * 7 : null;
      if (compStdOnSeven != null) {
        const gap = compStdOnSeven - overall360;
        const consistency = Math.abs(gap) < 0.5 ? '与素质项 360° 整体水位高度一致，结论可交叉佐证' : gap > 0 ? '高于素质项 360° 整体水位（折算后差 +' + gap.toFixed(2) + ' 分），提示岗位维度评价更偏积极，可关注是否存在单视角偏差' : '低于素质项 360° 整体水位（折算后差 ' + gap.toFixed(2) + ' 分），提示岗位胜任力较保守，结合多源 360° 评价需在岗位关键行为上加强证据沉淀';
        lines.push(`③ 多源印证：胜任力均分折算到 0～7 标尺约 ${compStdOnSeven.toFixed(2)} 分，${consistency}。`);
      }
    }
    if (hp?.resultLabel != null) {
      const hpLabel = String(hp.resultLabel);
      const cell = nb?.finalCell != null ? String(nb.finalCell) : null;
      const std = nb?.qualityStandardScore != null ? String(nb.qualityStandardScore) : null;
      const perf = performanceScore?.score != null ? String(performanceScore.score) : null;
      const evidence: string[] = [];
      if (cell) evidence.push(`九宫格落位 ${cell}`);
      if (std) evidence.push(`素质标准分 ${std}`);
      if (perf) evidence.push(`绩效综合分 ${perf}`);
      if (weighted != null) evidence.push(`岗位胜任力加权 ${weighted.toFixed(2)}`);
      lines.push(`④ 高潜结论：本期高潜识别为「${hpLabel}」${evidence.length ? `，证据链覆盖 ${evidence.join(' / ')}` : ''}。建议${/加速|高潜|保持/.test(hpLabel) ? '在加速池机制内安排关键事件复盘与跨部门历练，3～6 个月窗口内积累一次"显著破圈"事件作为下一轮晋升的事实证据' : '继续保持现有节奏，在下一周期重点突破短板维度后再行复评'}。`);
    }
    if (lines.length) {
      const focus = bottomComp?.axis;
      const concernDim = effectiveRadar.filter((r) => r.avg < qa.concernMeanThreshold).map((r) => r.label || r.code)[0];
      const reco: string[] = [];
      if (focus) reco.push(`将「${focus}」纳入个人发展计划，绑定 1 个具体场景与 1 名导师`);
      if (concernDim) reco.push(`「${concernDim}」短板维度配套行为级 KPI，月度 1on1 中复盘事实证据`);
      reco.push('保持高潜加速池节奏，每季度沉淀 1 份关键项目复盘材料以厚积证据链');
      lines.push(`⑤ 后续动作：${reco.map((r, i) => `${i + 1}）${r}`).join('；')}。`);
    }
    return lines;
  }, [competencyRadar, competency, competencyEvaluatorLabel, effectiveRadar, hp, nb, performanceScore, qa.concernMeanThreshold]);

  const overallSummary = useMemo<Array<{ title: string; paragraphs: string[] }>>(() => {
    const sections: Array<{ title: string; paragraphs: string[] }> = [];
    const cell = nb?.finalCell != null ? String(nb.finalCell) : '—';
    const hpLabel = hp?.resultLabel != null ? String(hp.resultLabel) : '—';
    const stdScore = nb?.qualityStandardScore != null ? String(nb.qualityStandardScore) : '—';
    const perfScore = performanceScore?.score != null ? String(performanceScore.score) : '—';
    const compScoreVal = competency?.weightedScore != null ? Number(competency.weightedScore) : null;
    const labelOf = (r: (typeof effectiveRadar)[number]) => r.label || r.code || '—';
    const advantages = effectiveRadar.filter((r) => r.avg >= qa.advantageMeanThreshold);
    const concerns = effectiveRadar.filter((r) => r.avg < qa.concernMeanThreshold);
    const overall360 = effectiveRadar.length ? effectiveRadar.reduce((a, b) => a + b.avg, 0) / effectiveRadar.length : null;
    const sortedRadar = [...effectiveRadar].sort((a, b) => b.avg - a.avg);
    const topRadar = sortedRadar[0];
    const bottomRadar = sortedRadar[sortedRadar.length - 1];
    const totals = (evaluationBreakdown ?? []).map((r) => r.rowTotal);
    const range = totals.length ? Math.max(...totals) - Math.min(...totals) : 0;
    const directRow = (evaluationBreakdown ?? []).find((r) => r.roleLabel === '直属领导');
    const crossRow = (evaluationBreakdown ?? []).find((r) => r.roleLabel === '跨部门同事');
    type DimCmp = NonNullable<typeof dimensionComparison>[number];
    const maxSpread = (dimensionComparison ?? []).reduce<DimCmp | null>((acc, c) => (!acc || Math.abs(c.spread ?? 0) > Math.abs(acc.spread ?? 0) ? c : acc), null);
    const competencyRow = competencyRadar ?? [];
    const compFocus = competencyRow.length ? [...competencyRow].sort((a, b) => a.score - b.score)[0].axis : null;
    const concernPriority = concerns.length ? labelOf(concerns[0]) : bottomRadar ? labelOf(bottomRadar) : '—';
    const isAccel = /加速|高潜|保持/.test(hpLabel);
    const isHH = /HH/.test(cell);
    const isHybrid = /HM|MH/.test(cell);
    const corePara: string[] = [];
    corePara.push(`本期落位：九宫格 ${cell}、高潜「${hpLabel}」、素质标准分 ${stdScore} / 绩效综合分 ${perfScore} / 岗位胜任力 ${compScoreVal != null ? compScoreVal.toFixed(2) : '—'}（${compScoreVal != null ? competencyBand(compScoreVal) : '—'}）。一句话定性：${isHH && isAccel ? '核心人才与重点晋升候选——绩效与潜力同时在位，是公司关键保留对象' : isHH ? '高位骨干，待加速识别信号确认是否进入晋升候选序列' : isHybrid ? '稳定贡献骨干，下一周期重点补强较弱的一面以争取"双 H"' : /MM/.test(cell) ? '中位人才，需通过 1～2 次关键事件兑现实现"出区"' : '当前需聚焦改进路径，先在下个周期内验证"出区"信号'}。`);
    corePara.push(`路线建议：${compScoreVal != null && compScoreVal >= 4.5 ? '已具备专家路线（原岗深耕到 4.5+）或 GM 路线（横向扩展业务幅面）的双向选项' : compScoreVal != null && compScoreVal >= 4.0 ? '建议先在原岗深耕到优秀段（≥ 4.5），再考虑横向扩展，避免胜任力评估被新场景重置' : '需先在岗位胜任力上补齐短板，待加权稳定到 4.0+ 段后再做路线选择'}；${isAccel ? '已被纳入加速通道，下一周期需提供"显著破圈"事件以支撑晋升评估' : '尚未触发加速机制，建议走"稳健提升 + 关键事件突破"的双轨路径'}。`);
    sections.push({ title: '一、核心定位与一句话结论', paragraphs: corePara });
    const keyPara: string[] = [];
    const strengthText = advantages.length ? advantages.map((r) => `「${labelOf(r)}」(${r.avg.toFixed(2)})`).join('、') : topRadar ? `「${labelOf(topRadar)}」${topRadar.avg.toFixed(2)}（暂未达「优势」阈值 ≥ ${qa.advantageMeanThreshold}）` : '—';
    const concernText = concerns.length ? concerns.map((r) => `「${labelOf(r)}」(${r.avg.toFixed(2)})`).join('、') : bottomRadar ? `「${labelOf(bottomRadar)}」${bottomRadar.avg.toFixed(2)}（未触发短板线但均值最低，是优先抓手）` : '—';
    keyPara.push(`素质画像（${effectiveRadar.length} 项 360° 均分 ${overall360 != null ? overall360.toFixed(2) : '—'}）：强项 ${strengthText}；短板 ${concernText}。${advantages.length >= 2 ? '强项已具"组合优势"，可作为对外标签使用；' : advantages.length === 1 ? '已形成 1 项明确强项，需要再沉淀 1～2 项形成强项组合；' : '强项尚未"出头"，下阶段需选定 1～2 个维度重点投入打造标志性能力；'}${concerns.length ? '短板维度若 1～2 个周期内未拉升，将形成"标签固化"，评审环节会本能跳过其晋升讨论。' : '当前未触发短板线，结构性风险较低，需警惕"高位高耗"——若 3～6 个月未沉淀新能力标签，会出现成长曲线钝化的隐性风险。'}`);
    if (totals.length && directRow && crossRow) {
      const distGap = directRow.rowTotal - crossRow.rowTotal;
      keyPara.push(`跨视角差异：${totals.length} 名评价人行合计极差 ${range.toFixed(1)} 分（直属 ${directRow.rowTotal.toFixed(1)} vs 跨部门 ${crossRow.rowTotal.toFixed(1)}）。${distGap >= 1.5 ? '跨部门视角明显保守于直属，对外可见度是阻力点——下一周期需通过协作邮件、共建项目里程碑、述职旁听让对外贡献"被看见"，目标把跨部门评分拉近直属 1 分以内' : distGap <= -1.5 ? '跨部门反而高于直属，对外形象优于内部预期，可借此扩大影响半径' : range < 3 ? '跨视角共识度较好，结论稳健可信' : '跨视角口径基本一致，仅部分维度存在差异，建议关键节点对齐口径'}。${maxSpread && Math.abs(maxSpread.spread ?? 0) >= 1 ? `自评盲区：「${maxSpread.dimName}」上自评${(maxSpread.spread ?? 0) > 0 ? '高于' : '低于'}他评 ${Math.abs(maxSpread.spread ?? 0).toFixed(2)} 分，存在认知偏差，需在 1on1 中校正。` : '自评与他评偏差均 < 1 分，自我画像与外部信号基本对齐，无显著认知盲区。'}`);
    }
    sections.push({ title: '二、强项 · 短板 · 跨视角风险', paragraphs: keyPara });
    const actionPara: string[] = [];
    const aiSuggestions = data.aiNarrative?.suggestions?.trim() ?? '';
    if (aiSuggestions) {
      const aiItems = splitNumberedActions(aiSuggestions);
      if (aiItems.length > 1) {
        const circled = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
        actionPara.push(
          `培养建议：${aiItems.map((it, i) => `${circled[i] ?? `${i + 1}.`} ${it}`).join('')}`,
        );
      } else {
        actionPara.push(`培养建议：${aiSuggestions}`);
      }
    }
    actionPara.push(`近期行动（90 天 ~ 6 个月）：① 针对${concerns.length ? concerns.map((r) => '「' + labelOf(r) + '」').join('、') : `「${concernPriority}」`}制定行为级提升计划，每月 1on1 留 ≥ 2 条具体行为案例；② 把${advantages.length ? advantages.map((r) => '「' + labelOf(r) + '」').join('、') : '当前最高分维度'}打造为对外可见标签，参与 1～2 个跨业务关键议题；③ 跨部门视角与直属差距缩小到 1 分以内${compFocus ? `，岗位胜任力上把「${compFocus}」作为优先突破点` : ''}。`);
    actionPara.push(`中长期方向（6 ~ 12 个月）：${isAccel ? '保持加速池节奏，每季度沉淀 1 份关键项目复盘材料，12 个月内积累一次"显著破圈"事件作为下一轮晋升的事实证据；与 HRBP 共同明确专家路线 vs GM 路线的方向感' : '通过短板补齐与强项放大，争取下一轮进入加速通道；担任 1～2 个关键项目主负责人，与直属一起明确下一职级所需的标志性能力清单'}。用人与留才：${isHH && isAccel ? '公司核心保留对象与重点晋升候选——薪酬激励、关键项目命名、长期股权等留才动作应同步前置' : (isHH || isHybrid) ? '中长期可信赖的业务骨干，留才以"项目机会 + 成长可见"为主，如出现非预期离职信号建议第一时间触发挽留' : '当前以"稳定贡献 + 重点观察"为主，留才动作聚焦"清晰路径 + 阶段性认可"，让本人感受到下一阶段成长方向清晰可达'}。`);
    actionPara.push(`下次盘点关注信号（≥ 3 项达成可维持或上行；≤ 1 项达成需启动中期 review）：① 跨部门 vs 直属差${directRow && crossRow && directRow.rowTotal - crossRow.rowTotal >= 1.5 ? `从当前 ${(directRow.rowTotal - crossRow.rowTotal).toFixed(1)} 分缩到 1 分内` : '维持在 1 分以内'}；② 短板维度${concerns.length ? `「${concerns.map((r) => labelOf(r))[0]}」是否突破 ${qa.concernMeanThreshold} 分上行` : `「${concernPriority}」是否进一步上探`}；③ 岗位胜任力加权${compScoreVal != null ? `是否从 ${compScoreVal.toFixed(2)} 拉到 ${Math.min(compScoreVal + 0.2, 5).toFixed(2)}+ 段` : '是否进入良好以上段'}；④ ${isAccel ? '6 个月内是否沉淀 ≥ 1 次"显著破圈"事件' : '12 个月内是否积累 ≥ 2 次主负责的关键项目交付'}。`);
    sections.push({ title: '三、培养建议及行动计划', paragraphs: actionPara });
    return sections;
  }, [nb, hp, performanceScore, competency, effectiveRadar, qa, evaluationBreakdown, dimensionComparison, competencyRadar, data.aiNarrative?.suggestions]);

  const dimComparisonColumns = useMemo(() => {
    const cols = [
      { title: '维度', dataIndex: 'dimName', width: 120 },
      { title: '自评', dataIndex: 'selfScore', width: 72, align: 'right' as const, render: (v: number | null | undefined) => (v != null ? v.toFixed(2) : '—') },
      { title: '直属', dataIndex: 'directScore', width: 72, align: 'right' as const, render: (v: number | null | undefined) => (v != null ? v.toFixed(2) : '—') },
      { title: '非自评均值', dataIndex: 'othersAvg', width: 96, align: 'right' as const, render: (v: number | null | undefined) => (v != null ? v.toFixed(2) : '—') },
      { title: '自评−他评', dataIndex: 'spread', width: 88, align: 'right' as const, render: (v: number | null | undefined) => (v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}` : '—') },
      { title: '说明', dataIndex: 'note' },
    ];
    return cols;
  }, []);

  const roleAverages = useMemo(
    () => computeRoleAverages(evaluationBreakdown ?? []),
    [evaluationBreakdown],
  );

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const collapseItems = useMemo(() => {
    const items: Array<{ key: string; label: string; children: React.ReactNode }> = [];

    if (effectiveRadar.length > 0) {
      items.push({
        key: 'quality-radar',
        label: multiEvaluatorRadarOption
          ? '素质项雷达（0～7 · 各评价人叠加）'
          : '素质项雷达（0～7 · 均值）',
        children: (
          <>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {multiEvaluatorRadarOption
                ? '同一坐标系下每名评价人一条闭合线，虚线为各维算术均值。'
                : '雷达点为该维度全部评价人（含自评）算术均分。'}
            </Typography.Paragraph>
            <LazyEChart
              active={expandedKeys.includes('quality-radar')}
              option={(multiEvaluatorRadarOption ?? simpleMeanRadarOption) as Record<string, unknown>}
              height={multiEvaluatorRadarOption ? 460 : 380}
            />
          </>
        ),
      });
    }

    if ((dimensionComparison?.length ?? 0) > 0) {
      const insufficientEvaluators = (evaluationBreakdown?.length ?? 0) < 2;
      items.push({
        key: 'dim-compare',
        label: '各维度自评 vs 他评对照',
        children: (
          <>
            <Table
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              columns={dimComparisonColumns}
              dataSource={(dimensionComparison ?? []).map((r, i) => ({ ...r, key: i }))}
            />
            <div className="report-dim-compare-gap" style={{ marginTop: 16 }}>
              <Typography.Title level={5} className="report-tab-section-title" style={{ marginTop: 0 }}>
                差异分析
              </Typography.Title>
              {insufficientEvaluators ? (
                <div className="report-360-empty-card">评价人数量不足，暂无法生成差异分析</div>
              ) : multiViewProfile ? (
                <Alert
                  type={
                    multiViewProfile.toneClass === 'good'
                      ? 'success'
                      : multiViewProfile.toneClass === 'warn'
                        ? 'warning'
                        : 'error'
                  }
                  showIcon
                  className="report-360-alert"
                  message={<strong>评价人分歧诊断</strong>}
                  description={
                    <div className="report-360-alert-body">
                      {multiViewProfile.narrative.kicker ? (
                        <div className="report-360-kicker">
                          <span className="report-360-kicker-label">综合判断</span>
                          {multiViewProfile.narrative.kicker
                            .split(/；|;/)
                            .map((s) => s.trim())
                            .filter(Boolean)
                            .map((line, i) => (
                              <p key={i}>{line.endsWith('。') ? line : `${line}。`}</p>
                            ))}
                        </div>
                      ) : null}
                      <Typography.Text type="secondary">{multiViewProfile.consensusNote}</Typography.Text>
                    </div>
                  }
                />
              ) : (
                <div className="report-360-empty-card">暂无分歧诊断数据</div>
              )}
            </div>
          </>
        ),
      });
    }

    if ((evaluationBreakdown?.length ?? 0) > 0) {
      items.push({
        key: 'eval-breakdown',
        label: '评价人打分明细',
        children: (
          <>
            {(evaluationBreakdown ?? []).map((row, idx) => (
              <div key={idx} className="report-eval-breakdown-block">
                <Typography.Text strong>
                  {row.roleLabel} · {row.evaluatorName}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                  行合计 {row.rowTotal.toFixed(1)}
                </Typography.Text>
                <Table
                  size="small"
                  pagination={false}
                  style={{ marginTop: 8 }}
                  columns={[
                    { title: '维度', dataIndex: 'dimName' },
                    { title: '得分', dataIndex: 'score', width: 72, align: 'right', render: (v: number) => v.toFixed(2) },
                    { title: '评语', dataIndex: 'comment', render: (v: string | null | undefined) => v ?? '—' },
                  ]}
                  dataSource={row.dimensions.map((d, i) => ({ ...d, key: i }))}
                />
              </div>
            ))}
          </>
        ),
      });
    }

    if (multiViewProfile) {
      items.push({
        key: 'eval-sketch',
        label: '评价人速写与异常视角',
        children: (
          <>
            {multiViewProfile.narrative.fragments.length > 0 ? (
              <ul className="report-sketch-list">
                {multiViewProfile.narrative.fragments.map((f, i) => (
                  <li key={i}>{f}。</li>
                ))}
              </ul>
            ) : (
              <Empty description="暂无速写" />
            )}
            {multiViewProfile.narrative.anchor ? (
              <div className="report-sketch-anchor">
                <span className="report-sketch-anchor-tag">定档锚点</span>
                {multiViewProfile.narrative.anchor}
              </div>
            ) : null}
            {roleAverages.length > 0 ? (
              <div className="report-360-roles" style={{ marginTop: 12 }}>
                {roleAverages.map((r) => (
                  <div key={r.roleLabel} className="report-360-role-card">
                    <div className="report-360-role-label">{r.roleLabel}</div>
                    <div className="report-360-role-value">{r.avg.toFixed(2)}</div>
                    <div className="report-360-role-sub">
                      {r.count} 人 · 标尺 0～7
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {multiViewProfile.anomalies.length > 0 ? (
              <List
                size="small"
                className="report-360-anomalies"
                style={{ marginTop: 12 }}
                dataSource={multiViewProfile.anomalies}
                renderItem={(a) => (
                  <List.Item className="report-360-anomaly-item">
                    <strong>{a.label}</strong>（{a.role} · {a.evaluator}，差{' '}
                    {a.delta >= 0 ? '+' : ''}
                    {a.delta.toFixed(2)} 分）：{a.note}
                  </List.Item>
                )}
              />
            ) : null}
            <Table
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              style={{ marginTop: 12 }}
              columns={[
                { title: '视角', dataIndex: 'role', width: 120 },
                { title: '评价人', dataIndex: 'evaluator', width: 140 },
                { title: '本视角均分', dataIndex: 'avg', width: 110, align: 'right', render: (v: number) => v.toFixed(2) },
                { title: 'vs 他视角均值', dataIndex: 'delta', width: 130, align: 'right', render: (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}` },
                { title: '状态', dataIndex: 'status', width: 110, render: (s: 'high' | 'low' | 'consistent') => s === 'high' ? '↑ 偏高' : s === 'low' ? '↓ 偏低' : '✓ 一致' },
              ]}
              dataSource={multiViewProfile.rows.map((r, i) => ({ ...r, key: i }))}
            />
            {multiViewProfile.flatRows.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="评估投入度提示"
                description={multiViewProfile.flatRows
                  .map(
                    (f) =>
                      `${f.role}「${f.evaluator}」在全部 ${f.dimCount} 项维度上一律给同一分（${f.sameScore} 分），未做差异化判断。`,
                  )
                  .join(' ')}
              />
            ) : null}
          </>
        ),
      });
    }

    return items;
  }, [
    dimComparisonColumns,
    dimensionComparison,
    effectiveRadar.length,
    evaluationBreakdown,
    expandedKeys,
    multiEvaluatorRadarOption,
    multiViewProfile,
    roleAverages,
    simpleMeanRadarOption,
  ]);

  return (
    <div id="evidence" className="report-tab-panel report-evidence-detail">
      <SectionTitle index="一" title="概览" subtitle="规则计算与原始评价证据" color="#176B5B" />
      <Card className="report-evidence-card" title="综合定位明细" size="small">
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="素质标准分">{nb?.qualityStandardScore != null ? String(nb.qualityStandardScore) : '—'}</Descriptions.Item>
          <Descriptions.Item label="素质档（池内）">{nb?.qualityLevel != null ? String(nb.qualityLevel) : '—'}</Descriptions.Item>
          <Descriptions.Item label="绩效综合分">{performanceScore?.score != null ? String(performanceScore.score) : '—'}</Descriptions.Item>
          <Descriptions.Item label="绩效档">{nb?.performanceLevel != null ? String(nb.performanceLevel) : performanceScore?.level != null ? String(performanceScore.level) : '—'}</Descriptions.Item>
          <Descriptions.Item label="九宫格格位" span={2}>
            <Tag color="blue">{nb?.finalCell != null ? String(nb.finalCell) : '—'}</Tag>
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>（纵轴素质 / 横轴绩效）</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="岗位胜任力加权">{competency?.weightedScore != null ? String(competency.weightedScore) : '—'}</Descriptions.Item>
          <Descriptions.Item label="高潜识别">{hp?.resultLabel != null ? String(hp.resultLabel) : '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <SectionTitle index="二" title="素质项评价" subtitle="基于 360° 多视角的维度水位 · 明细表" color="#527067" />
      <AbilityAnalysis effectiveRadar={effectiveRadar} />
      {collapseItems.length > 0 ? (
        <Collapse
          className="report-evidence-collapse"
          activeKey={expandedKeys}
          onChange={(keys) => setExpandedKeys(Array.isArray(keys) ? keys : [keys])}
          items={collapseItems}
        />
      ) : (
        <Empty description="暂无素质项评价明细" />
      )}

      <Card title="素质项评价汇总分析" size="small">
        {qualityOverallAnalysis.length > 0 ? (
          <List size="small" dataSource={qualityOverallAnalysis} renderItem={(item) => <List.Item className="report-analysis-line">{item}</List.Item>} />
        ) : (
          <Empty description="暂无素质项评价汇总" />
        )}
      </Card>

      <SectionTitle index="三" title="岗位胜任力 · 高潜" subtitle="岗位维度雷达 · 加权综合分 · 高潜证据链" color="#527067" />
      {competencyOption || competency?.weightedScore != null ? (
        <section className="report-ability-analysis" aria-label="岗位胜任力雷达">
          <div className="report-ability-ranks">
            <div className="report-rank-title report-rank-title--adv">岗位胜任力雷达（1～5 分）</div>
            {competency?.weightedScore != null ? (
              <div className="report-competency-summary">
                加权综合分{' '}
                <strong>
                  {Number(competency.weightedScore).toFixed(2)}
                </strong>
                {' · '}
                {competencyBand(Number(competency.weightedScore))}
                {competencyEvaluatorLabel ? ` · ${competencyEvaluatorLabel}` : ''}
              </div>
            ) : null}
            {competencyTop3.length > 0 ? (
              <ul className="report-rank-list">
                {competencyTop3.map((item, i) => (
                  <li key={item.axis} className="report-rank-item">
                    <div className="report-rank-meta">
                      <span className="report-rank-index">{i + 1}</span>
                      <span className="report-rank-label">{item.axis}</span>
                      <span className="report-rank-score">{item.score.toFixed(2)}</span>
                    </div>
                    <div className="report-rank-bar-track">
                      <div
                        className="report-rank-bar-fill report-rank-bar-fill--adv"
                        style={{ width: `${(item.score / 5) * 100}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
            {competencyBottom2.length > 0 ? (
              <>
                <div className="report-rank-title report-rank-title--improve" style={{ marginTop: 12 }}>
                  待提升 TOP2
                </div>
                <ul className="report-rank-list">
                  {competencyBottom2.map((item, i) => (
                    <li key={item.axis} className="report-rank-item">
                      <div className="report-rank-meta">
                        <span className="report-rank-index">{i + 1}</span>
                        <span className="report-rank-label">{item.axis}</span>
                        <span className="report-rank-score">{item.score.toFixed(2)}</span>
                      </div>
                      <div className="report-rank-bar-track">
                        <div
                          className="report-rank-bar-fill report-rank-bar-fill--improve"
                          style={{ width: `${(item.score / 5) * 100}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            {!competencyOption && competency?.weightedScore != null ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="未返回分项维度得分，暂无法绘制胜任力雷达图"
              />
            ) : null}
          </div>
          <div className="report-ability-radar">
            {competencyOption ? (
              <ReactECharts option={competencyOption} style={{ height: 240 }} notMerge lazyUpdate />
            ) : (
              <div className="report-ability-empty">暂无岗位胜任力雷达数据</div>
            )}
          </div>
        </section>
      ) : null}
      <Card title="岗位胜任力与高潜汇总分析" size="small">
        {competencyHpAnalysis.length > 0 ? (
          <List size="small" dataSource={competencyHpAnalysis} renderItem={(item) => <List.Item className="report-analysis-line">{item}</List.Item>} />
        ) : (
          <Empty description="暂无胜任力与高潜汇总" />
        )}
      </Card>

      <SectionTitle index="四" title="报告总结" subtitle="多源交叉后的整体判断 · 培养建议及行动计划 · 直属上级评语" color="#527067" />
      {!!overallSummary.length && (
        <Card title="综合总结（基于本期全部盘点数据 · 多源交叉判断）" size="small">
          <List
            size="small"
            dataSource={overallSummary}
            renderItem={(item) => {
              const isDevPlan = item.title.includes('培养建议及行动计划');
              const aiQueueStatus = data.aiQueueStatus ?? (data.aiNarrative ? 'READY' : 'NONE');
              const aiBusy = aiQueueStatus === 'PENDING' || aiQueueStatus === 'RUNNING';
              const showGenerate =
                isDevPlan &&
                !(data.aiNarrative?.suggestions?.trim()) &&
                data.aiEnabled &&
                !!onGenerateNarrative;
              return (
                <List.Item
                  className="report-summary-block"
                  id={isDevPlan ? 'development' : undefined}
                >
                  <Typography.Title level={5}>{item.title}</Typography.Title>
                  {item.paragraphs.map((p, i) => (
                    <SummaryParagraph key={i} text={p} />
                  ))}
                  {showGenerate ? (
                    <div className="report-dev-empty" style={{ marginTop: 8 }}>
                      <Typography.Text type="secondary">暂无 AI 培养建议</Typography.Text>
                      <Button
                        type="primary"
                        size="small"
                        style={{ marginTop: 12, display: 'block' }}
                        loading={generateLoading || aiBusy}
                        disabled={aiBusy}
                        onClick={onGenerateNarrative}
                      >
                        {aiQueueStatus === 'FAILED' ? '重新生成 AI 摘要' : '生成 AI 摘要'}
                      </Button>
                    </div>
                  ) : null}
                </List.Item>
              );
            }}
          />
        </Card>
      )}
      {leaderComment ? (
        <Card title="直属上级建议" size="small">
          <Typography.Paragraph style={{ marginBottom: 0, lineHeight: 1.85 }}>{leaderComment}</Typography.Paragraph>
        </Card>
      ) : null}
    </div>
  );
}
